import { Suspense, useState } from 'react'

import { MessageSquare, Menu as MenuIcon, X } from 'lucide-react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'

import dotTile from '@/assets/illustrations/dot-tile.svg'
import { BrandIcon } from '@/components/BrandIcon'
import { ADMIN_SECTIONS, sectionByPath } from '@/components/shell/sections'
import { UserMenu } from '@/components/shell/UserMenu'
import { Button, IconButton } from '@/components/ui/Button'
import { LoadingState } from '@/components/ui/states'
import { FeedbackConfig } from '@/config/feedback'
import { cn } from '@/lib/cn'

/// Оболочка админки: тёмный сайдбар слева, шапка сверху, контент справа.
///
/// Во Flutter-версии раздел жил в состоянии виджета и в адресной строке не
/// отражался — перезагрузка возвращала на дашборд, а ссылкой на раздел
/// поделиться было нечем. Здесь раздел — это путь, и оба сценария работают.
export function AdminShell() {
  const location = useLocation()
  const section = sectionByPath(location.pathname)
  const [drawerOpen, setDrawerOpen] = useState(false)

  return (
    <div className="flex h-full overflow-hidden bg-canvas">
      {/* Сайдбар: постоянный на широком экране, выдвижной на узком. */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-[260px] flex-col bg-sidebar',
          'transition-transform duration-300 ease-out-soft lg:static lg:translate-x-0',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        // Точечная сетка вместо плоской заливки: тёмная панель во всю высоту
        // экрана без неё выглядит дырой в макете.
        style={{ backgroundImage: `url(${dotTile})`, backgroundRepeat: 'repeat' }}
      >
        <div className="flex items-center gap-3 px-5 pt-5 pb-2">
          <BrandIcon size={40} radius={12} className="shadow-brand" />
          <div className="min-w-0 flex-1">
            <p className="text-lg font-extrabold text-white">KHANQ</p>
            <p className="text-xs text-sidebar-text-faint">Admin Panel</p>
          </div>
          <IconButton
            label="Закрыть меню"
            onClick={() => setDrawerOpen(false)}
            className="text-sidebar-text-faint hover:bg-sidebar-selected hover:text-white lg:hidden"
          >
            <X size={18} />
          </IconButton>
        </div>

        <nav className="mt-6 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-4 pb-4">
          {ADMIN_SECTIONS.map((item, index) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              onClick={() => setDrawerOpen(false)}
              style={{ animationDelay: `${index * 35}ms` }}
              className={({ isActive }) =>
                cn(
                  'group relative flex animate-slide-in-left items-center gap-3 rounded-xl px-3.5 py-3 text-sm',
                  'transition-colors duration-200 ease-out-soft',
                  isActive
                    ? 'bg-sidebar-selected font-semibold text-white'
                    : 'font-medium text-sidebar-text hover:bg-sidebar-hover hover:text-white',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {/* Метка активного раздела: короткая полоска у левого края,
                      которая вырастает вместе с переходом. */}
                  <span
                    aria-hidden
                    className={cn(
                      'absolute top-1/2 left-0 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-sidebar-accent',
                      'origin-center transition-transform duration-200 ease-out-soft',
                      isActive ? 'scale-y-100' : 'scale-y-0',
                    )}
                  />
                  <item.icon
                    size={20}
                    className={cn(
                      'shrink-0 transition-transform duration-200 ease-out-soft group-hover:scale-110',
                      isActive ? 'text-sidebar-accent' : 'text-sidebar-text-faint',
                    )}
                  />
                  {item.title}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-5 py-4">
          <UserMenu dark />
        </div>
      </aside>

      {drawerOpen ? (
        <button
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-40 animate-fade-in bg-ink/40 backdrop-blur-[2px] lg:hidden"
        />
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Шапка отделена от холста подложкой и тенью — линейки под ней нет. */}
        <header className="z-30 flex h-16 shrink-0 items-center gap-3 bg-surface px-4 shadow-card lg:px-7">
          <IconButton label="Меню" onClick={() => setDrawerOpen(true)} className="lg:hidden">
            <MenuIcon size={20} />
          </IconButton>

          <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-tint text-brand">
            <section.icon size={18} />
          </span>
          <h1 key={section.path} className="animate-fade-in truncate text-xl font-bold text-ink">
            {section.title}
          </h1>

          <div className="ml-auto flex items-center gap-3">
            <Button
              variant="outlined"
              size="sm"
              icon={<MessageSquare size={16} />}
              className="hidden sm:inline-flex"
              onClick={() => window.open(FeedbackConfig.responsesUrl, '_blank', 'noopener,noreferrer')}
            >
              Обратная связь
            </Button>
            <div className="hidden lg:block">
              <UserMenu />
            </div>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto">
          {/* Раздел приезжает отдельным чанком; спиннер стоит внутри рабочей
              области, а шапка и сайдбар при переходе не мигают. Ключ по пути
              перезапускает появление — переход между разделами виден. */}
          <Suspense fallback={<LoadingState />}>
            <div key={location.pathname} className="animate-fade-up">
              <Outlet />
            </div>
          </Suspense>
        </main>
      </div>
    </div>
  )
}
